import posthog from "posthog-js";
import type { ShareChannel, ShareSurface } from "@/lib/share";

/**
 * Typed analytics events (CM-2).
 *
 * One place that owns every custom PostHog event name + its property shape, so
 * event names stay consistent across the app and typos become type errors
 * rather than silently-diverging funnels. Add a new event by extending
 * `AnalyticsEvents`; `capture()` then enforces the correct props at every call
 * site.
 *
 * ---------------------------------------------------------------------------
 * THE TAXONOMY
 * ---------------------------------------------------------------------------
 * Two tiers, per the portfolio standard (Control Center
 * `docs/funnel-instrumentation.md`), and the Control Center's per-product
 * funnel registry (`src/lib/domain/product-funnel.ts`) maps each one to a
 * position in this product's funnel:
 *
 *   SPINE — the portfolio-wide conversion vocabulary. `checkout_start`,
 *   `purchase`, `signup_complete`, `subscription_cancel`. Same meaning on
 *   every product so one dashboard reads all of them.
 *
 *   ENGAGEMENT — the things that only exist here: searches, tabs, alerts,
 *   saved jobs, the nudge, the portfolio pitch, a free source suggestion.
 *   These are the actions we built for people to take, and the Control Center
 *   scores each one against the goal event — of the people who did it, what
 *   share reached an account or a payment, versus the baseline. That is what
 *   turns "we should push saved jobs" into a claim that can be wrong.
 *
 * Rules that are not negotiable:
 *   - **Names are immutable once live.** Renaming splits the series and every
 *     historical comparison silently breaks. This is why the sign-in step is
 *     still `magic_link_requested` and not the spine's `signup_start`, and why
 *     `listing_checkout_started` keeps firing beside `checkout_start`: the
 *     Control Center maps names to funnel positions so a product never has to
 *     rename an event to be readable.
 *   - **Never interpolate a value into a name.** `purchase` with
 *     `{kind: "premium"}`, never `premium_purchase`.
 *   - **Money and account creation fire server-side**
 *     (`lib/analytics/server.ts`). A client-side purchase counts a redirect
 *     completing, not a payment.
 *   - **No free text in properties**, except where a field already exists and
 *     is explicitly reviewed for it. Search terms are never sent: the PostHog
 *     config strips query strings from every URL, and an event carrying `q`
 *     would walk straight around that decision. Send shape (`has_query`,
 *     `filters`, `results`), never content.
 */
export interface AnalyticsEvents {
  // ---- spine (client half; the rest fires server-side) ----------------------
  /**
   * Checkout opened. Fires just BEFORE the redirect via `captureBeforeRedirect`
   * — a plain capture races `window.location.href` and the step is simply lost,
   * which is why checkout funnels routinely show more purchases than starts.
   */
  checkout_start: {
    /** Which of the two businesses. Never fold this into the event name. */
    kind: "premium" | "posting";
    /** Which Premium button started it (go_live | settings | premium_page). */
    surface?: string;
  };

  // ---- candidate: search ---------------------------------------------------
  /**
   * A real search — a query, a filter, or both. Browsing the bare board is not
   * a search; counting it would make the step meaningless. No search terms are
   * sent, only the shape of the search and what came back.
   */
  job_search: {
    has_query: boolean;
    /** How many filters were active — the shape of the search, not its content. */
    filters: number;
    /** Results the server actually returned. 0 is the interesting case. */
    results: number;
  };
  /** Filters were active on this search — carried separately as an assist. */
  search_filter_apply: { filters: number };

  // ---- candidate: jobs -----------------------------------------------------
  /** A job detail page was opened. */
  job_view: { job_id: number; org_key: string };
  /** Candidate clicked through to an official apply URL on a job page. */
  apply_click: {
    job_id: number;
    org_key: string;
    /** Which apply button — the layout differs by breakpoint. */
    placement: "sidebar" | "mobile";
  };
  /** A job was saved to My Jobs. Signed-in only, so it is deep engagement. */
  job_save: { job_id: number; surface: string };
  /** Candidate flagged a problem with a listing (dead link, wrong info, etc). */
  job_issue_reported: {
    job_id: number;
    org_key: string;
  };
  /** Candidate hit a zero-result search and clicked through to tell us what's missing. */
  no_results_feedback_clicked: {
    query: string;
    filter_count: number;
  };

  // ---- candidate: acquisition surfaces -------------------------------------
  /**
   * Landed on an SEO surface (location / category / org / guide) rather than
   * the board. Search Console showed 144 landing pages drawing impressions and
   * zero clicks; this is the on-site half of that question — of the people who
   * do arrive on one, do any of them go on to convert?
   */
  seo_landing_view: { surface: "location" | "category" | "organization" | "guide" };
  /** Visitor clicked a "start / create your portfolio" CTA. */
  portfolio_cta_click: {
    /** Which CTA fired it — for funnel segmentation. */
    location: "hero" | "product" | "final_cta" | "job_detail_sidebar" | "job_detail_mobile";
  };
  /**
   * The post-apply offer's own CTA (PostApplyPanel) — "create a video
   * application" for the job just applied to. Separate from
   * portfolio_cta_click on purpose: it sells a different thing (T-246), and
   * mixing it into that series would hide whether the reframe worked.
   */
  video_app_cta_click: { job_id: number };
  /** A candidate published their portfolio from the dashboard. */
  portfolio_published: Record<string, never>;
  /**
   * Ported from internationaljobs (CM-140/CM-277), 2026-10-09 (T-284): that
   * board's dwell-based paywall survey is the proven answer to the question
   * this board's premium_pitch_dismissed FeedbackLink couldn't — it asks
   * without requiring a click first. Same event names/shape on purpose, so
   * the two boards stay comparable.
   */
  micro_survey_answered: {
    /** Stable question id — answers stay comparable when the copy changes. */
    survey_id: string;
    answer: string;
    /** Which affordance produced this answer — a one-tap quickOption button
     * vs. the typed textarea — so response-rate analysis can tell the two
     * paths apart instead of lumping them together. */
    method: "quick_option" | "free_text";
  };
  /** The micro-survey card actually rendered — pairs with micro_survey_answered
   * to compute a real response rate instead of guessing whether 0 answers means
   * 0 impressions or 0% completion. */
  micro_survey_shown: {
    survey_id: string;
  };

  // ---- candidate: email + account ------------------------------------------
  /** Alert preferences were edited rather than abandoned. Read against unsubscribe. */
  alert_prefs_update: { action: "create" | "update" | "delete" };
  /**
   * /auth/login was reached (client-side, once per mount) — `intent` is the
   * SAME 3-way bucket `introFor()` already computes server-side to choose the
   * page's headline (jobs/alerts vs post-a-job vs the default portfolio
   * pitch), sent as shape not content so it doesn't reopen the query-string
   * minimisation `sanitize_properties` exists for.
   *
   * Added 2026-09-08: `$pageview` already shows 52 /auth/login visits/30d,
   * but `sanitize_properties` strips every URL's query string before it
   * reaches PostHog — including `next`, so no `$pageview` property nor any
   * existing click event (`login_cta_click` totals only 3/30d; the other
   * routes here are ALL server-side `redirect()` calls from protected pages —
   * `/dashboard`, `/post-a-job/new`, `/my-jobs`, `/dashboard/applications*` —
   * which fire no client event at all) can currently explain who the other
   * ~49 visits/30d actually are. This is the primary directive's weakest
   * step (signups 0.4%) with its single biggest reach gap unmeasured; this
   * event is the diagnostic before any copy/placement guess on this page.
   */
  login_page_view: { intent: "jobs" | "post_a_job" | "portfolio" };
  /**
   * Visitor submitted the magic-link sign-in form and Supabase accepted the
   * request. Does NOT mean the email arrived or the link was clicked — see
   * PRIORITY.md 0s: Supabase Auth has no custom SMTP wired, so this event
   * existing with zero matching sign-ins is the signal that would confirm
   * the mailer (not the form) is where signups are being lost. This is the
   * funnel's "started signing up" step; `signup_complete` (server-side) is
   * the step after it, and the gap between them IS the SMTP blocker.
   */
  magic_link_requested: Record<string, never>;
  /**
   * The email field on /auth/login was focused (once per mount), not
   * necessarily submitted. Added 2026-09-03: 30d PostHog shows /auth/login at
   * only 29 pageviews against 7 magic_link_requested (24%) — small enough
   * that guessing at a copy fix would repeat the disproven job-alert-form
   * pattern (see JobAlertCapture's job_alert_email_focus, the same split).
   * This distinguishes "never engaged with the form at all" (a reach/
   * motivation question — the page itself may be fine) from "typed an email
   * and still bailed" (a trust/last-mile question) before any redesign is
   * attempted. See PRIORITY.md checkpoint 2026-09-03 ~15:50-16:40 UTC.
   */
  login_email_focus: Record<string, never>;
  /**
   * A visitor clicked a link/button that routes to `/auth/login` from
   * somewhere OTHER than a protected-page redirect. Added 2026-09-05: at
   * least 4 distinct surfaces already link there (`HeaderCta`'s "Log in" and
   * "Sign up", `SaveJobButton`'s signed-out bookmark, `SearchTabs`' "Sign in
   * to save"), on top of `AlertConfirmedModal`'s already-tracked
   * `job_alert_upgrade_click` — but only 33 `/auth/login` pageviews/30d
   * (1.3% of visitors) with no way to tell which surface, if any, is
   * actually driving that reach. `surface` splits it apart before the next
   * reach experiment guesses at the wrong one.
   */
  login_cta_click: { surface: string };
  /**
   * The sign-in link a visitor clicked bounced them back to /auth/login with
   * an `?error=` code instead of a session (T-256). Before this, the login
   * page silently dropped the code and rendered the plain empty form — a
   * real user described it as "trying to get verified, the second time I
   * have wasted time trying to do this," which is exactly this: a dead link
   * with zero on-screen explanation, so they had no way to tell "try again"
   * from "you're stuck." `reason` is the same code /auth/callback and
   * /auth/confirm already redirect with (exchange_failed, no_user,
   * missing_code, unconfigured) — this is the first time any of them are
   * visible in analytics rather than only in the URL bar.
   */
  login_link_error_shown: { reason: string };

  // ---- sharing -------------------------------------------------------------
  /**
   * The share UI was opened. The denominator for share_click: a big gap between
   * the two means the affordance is found but the channels are wrong.
   */
  share_open: { surface: ShareSurface };
  /**
   * Something was actually shared. `native` means the OS share sheet, which
   * does not report which app was chosen — we do not guess.
   */
  share_click: { surface: ShareSurface; channel: ShareChannel };
  /**
   * Someone ARRIVED on a shared link. This is the half that matters: sharing is
   * only worth building if the people it sends convert, and the Control Center
   * scores this row's conversion against the all-visitor baseline. Fired from
   * the UTM parameters, so it also covers links pasted onward by hand.
   */
  share_visit: { surface: string; channel: string };

  // ---- candidate: premium --------------------------------------------------
  /** The Premium offer was actually put in front of someone. */
  premium_view: { surface: string };
  /**
   * The free-tier "Your plan" card on /dashboard (the un-gated Premium pitch +
   * SubscribeButton, no dialog click required to see it) scrolled into view.
   * Distinct from `premium_view`, which only fires for the click-to-open
   * UpgradeDialog on OTHER surfaces (locked-feature prompts) — this dashboard
   * placement never opens that dialog, so without this event there is no way
   * to tell "never scrolled to it" apart from "saw it and didn't click
   * Subscribe" when `checkout_start` stays at zero.
   */
  premium_pitch_view: { surface: string };
  /**
   * A Premium button was clicked — "Go live" on the page editor, Subscribe in
   * Settings or on the Premium page (`surface` says which). Fires before any
   * checkout exists, so a click that then failed still counts. Same event and
   * property as Change Maker; the Control Center shows it as "Clicked Go live /
   * Subscribe" (2026-09-21).
   */
  premium_checkout_attempted: { surface: string };
  /** The click did not get a checkout session: our bug, not their decision. */
  premium_checkout_failed: { surface: string; reason?: string; client_exception?: boolean };
  /**
   * A locked-feature prompt (video intro, project video, video applications,
   * the applications-list/new/detail walls) scrolled into view. `LockedFeature`
   * and `UpgradeWall` previously fired nothing until clicked — `premium_view`
   * only covers the dialog actually opening — so a surface with zero clicks
   * was indistinguishable from a surface nobody ever scrolled to.
   */
  locked_feature_view: { surface: string };
  /**
   * T-306: a signed-out visitor arriving at `/dashboard/applications/new`
   * (the post-apply video-application CTA) saw the no-account example
   * preview instead of being bounced straight to `/auth/login`. The
   * 2026-10-26 checkpoint found that page asked for sign-in AND payment
   * before showing anything resembling the product — this event measures
   * whether seeing the product first changes anything about the sign-in
   * rate that follows.
   */
  no_account_preview_view: { surface: string };
  /** The preview's "Sign in free to draft yours" CTA was clicked. */
  no_account_preview_cta_click: { surface: string };

  // ---- employer ------------------------------------------------------------
  /** The post-a-job landing page was opened — top of the employer funnel. */
  post_job_view: Record<string, never>;
  /** The employer began filling the vacancy form (first field touched). */
  listing_form_start: Record<string, never>;
  /**
   * The "Hiring at X?" CTA on an org or job page was clicked through to
   * /post-a-job. Org/job pages are where employer-side staff actually land
   * (/post-a-job itself gets ~0 direct pageviews), so this is the only
   * measurable top of the employer funnel.
   */
  org_claim_cta_click: { org_key: string };
  /**
   * An employer submitted a vacancy through the free review form on
   * /post-a-job — the UNPAID path. It feeds coverage rather than revenue, and
   * keeping it distinct from `purchase` is the only way to tell whether the
   * paid offer is being rejected or simply never seen.
   */
  employer_job_submitted: {
    org_type: string;
    contract_type: string;
  };

  // ---- feedback / research -------------------------------------------------
  /**
   * A `FeedbackLink` widget was opened (the inline textarea revealed) but not
   * necessarily submitted. Added 2026-09-01: `feedback_submitted` sat at
   * ZERO across every surface (exit-intent, report-a-listing, missing-
   * employer, coverage) for a full 30 days with no way to tell "nobody ever
   * clicks the prompt" from "people open it and abandon the form" — this is
   * the missing middle step of that funnel.
   */
  feedback_opened: { kind: string };
  /**
   * A visitor completed one of the `FeedbackLink` widgets (report-a-listing,
   * missing-employer, coverage). Captured with the free-text length (not the
   * text itself — PII risk) so volume is visible even on a run where
   * RESEND_API_KEY isn't configured and `delivered` in the action result is
   * false. `feedback-actions.ts` holds the actual server-side delivery.
   *
   * T-267 (2026-10-02): "1 message sent to us never arrived" — turned out
   * `delivered: false` (IP rate-limited or Resend failed) discarded the
   * typed text everywhere: no DB table backs this widget, so a failed send
   * was unrecoverable, yet `FeedbackLink` still told the visitor "we've got
   * it." `message_on_failure` closes that gap: sent ONLY when `delivered` is
   * false, so the already-decided PII tradeoff for the common/successful
   * path (email only, no text in PostHog) is untouched — this is purely the
   * fallback for the rare case where the text would otherwise vanish.
   */
  feedback_submitted: {
    kind: string;
    message_length: number;
    delivered: boolean;
    message_on_failure?: string;
  };
  /**
   * The post-apply box asked for an email (T-247). `apply_click` is the only
   * proven-intent moment on the site — 677 in a week against 9 addresses left
   * (0.3% of visitors) — so the alert offer moved here, where the person has
   * just finished applying rather than started browsing.
   */
  post_apply_alert_submit: {
    job_id: number;
  };
  /**
   * The alert row was stored (`check_inbox`) or already existed
   * (`already_on`). This, not the submit, is the conversion: the row is what
   * we can mail.
   */
  post_apply_alert_captured: {
    job_id: number;
    state: string;
  };
  /**
   * RETIRED 2026-09-30 (same day): the inline form is back, and the 0.07%
   * below was a bug, not a verdict — its checkbox sent terms=1 and the action
   * only accepted "on", so every submit was refused. Kept for history.
   * Replaced `post_apply_alert_submit`/`post_apply_alert_captured` as the
   * free-alert ask inside `PostApplyPanel` (2026-09-30): that mini-form
   * converted 1336 `post_apply_survey_seen` → 1 submit (0.07%), a ~20x worse
   * rate than the identical offer via the plain, ungated `JobAlertCapture` at
   * the bottom of the same page (2031 views → 31 successes, 1.5%, same 30d
   * window). Rather than iterate on a second copy of the same form, the panel
   * now links down to the working one. This event is the new funnel's first
   * step; a click that doesn't show up in `job_alert_cta_view`/`submit`
   * shortly after means the anchor-scroll itself isn't landing.
   */
  post_apply_alert_link_click: {
    job_id: number;
  };
  /**
   * The visitor's focus returned to this tab after Apply opened the employer's
   * ATS in a new one (`target=_blank`), while the post-apply survey was still
   * unanswered. post_apply_survey_response has sat at 0 against 71
   * apply_click/30d with no way to tell "never came back to this tab" from
   * "came back and still ignored the survey" — this splits the gap. See
   * PRIORITY.md 2026-08-19.
   */
  post_apply_tab_return: {
    job_id: number;
    org_key: string;
  };
  /**
   * The post-apply box actually entered the viewport after `post_apply_tab_return`'s
   * `scrollIntoView` ran. 2026-08-24: this IS the remaining gap — 30d totals
   * were apply_click=144, tab_return=39 (27%), but survey_seen=only 7 (18%
   * of returns). The mechanical-miss half of the 2026-08-22 hypothesis won:
   * `scrollIntoView({behavior:"smooth"})` was very likely getting
   * interrupted by the hidden->visible transition it fires on. Fixed in
   * PostApplySurvey.tsx (instant scroll, lower IntersectionObserver
   * threshold, wider window) — watch whether survey_seen/tab_return climbs
   * before drawing conclusions about survey_response itself. See
   * PRIORITY.md 2026-08-24.
   */
  post_apply_survey_seen: {
    job_id: number;
    org_key: string;
  };
  /**
   * Real input (mouse move/scroll/tap/key) within 8s of `post_apply_survey_seen`
   * — added 2026-09-28 because `survey_seen` now tracks `tab_return` almost
   * 1:1 (401 vs 400 in 6d, up from the 18% the 2026-08-24 scroll fix solved),
   * yet `video_app_cta_click` + `post_apply_alert_captured` are BOTH still 0
   * across all ~400 of those "seen" impressions — including the zero-friction
   * free alert, not just the paid one. `scrollIntoView` proves the box entered
   * the viewport; it proves nothing about whether a human was looking at the
   * screen at that moment (closing the ATS tab can return focus to this one
   * with nobody attending to it). This event is the next diagnostic step:
   * genuinely near-zero engaged-rate would point at attention/dwell, a
   * healthy engaged-rate with still-zero clicks would point at the offer
   * itself. Don't change the panel's copy or CTAs again until this read.
   */
  post_apply_engaged: {
    job_id: number;
    org_key: string;
  };
  /**
   * Exit-intent feedback prompt (`ExitIntentFeedback.tsx`) offered itself —
   * mouse left toward the top of the viewport after a minimum dwell, no
   * capture surface exists yet for the much larger non-converting-visitor
   * population (everyone who never reaches apply_click or a signup). `path`
   * is the page it fired on, so it's visible which templates actually
   * generate "about to leave" moments worth asking about. `trigger` added
   * 2026-09-05: the original `mouseout` heuristic has no touch equivalent, so
   * ~34% of pageviews (mobile, PostHog device-type split) never saw this
   * prompt at all — a `scroll` trigger (deep scroll + dwell, mobile only)
   * covers that population. Split by `trigger` before comparing conversion
   * rates between the two; they are not interchangeable.
   */
  exit_intent_shown: {
    path: string;
    trigger: "mouseout" | "scroll";
  };
  /** Closed the exit-intent prompt without submitting feedback. */
  exit_intent_dismissed: {
    path: string;
    trigger: "mouseout" | "scroll";
  };

  // ---- field performance -----------------------------------------------------
  /**
   * A Core Web Vital measured on a real visitor's device (RUM), via Next.js's
   * `useReportWebVitals`. The mobile LCP the 2026-08-25 PageSpeed lab audit
   * found (5.2-5.7s, "poor") had zero field-data confirmation — PostHog had no
   * `$web_vital*`-shaped event at all — so there was no way to tell how many
   * *real* visitors, on real devices/networks, actually experienced that. This
   * closes that gap. One event per metric per page load, not just LCP: CLS/INP
   * regressions are invisible to a single-URL lab run but show up here.
   */
  web_vital: {
    metric: "LCP" | "CLS" | "INP" | "FCP" | "TTFB";
    value: number;
    rating: "good" | "needs-improvement" | "poor";
    /** Path only, no query string — same minimisation as every other URL prop. */
    path: string;
  };
}

/**
 * Capture a typed event. No-ops until PostHog is loaded (it only loads after
 * consent — see PostHogProvider), so calling this before consent is safe and
 * simply drops the event, exactly like the inline captures elsewhere.
 */
export function capture<K extends keyof AnalyticsEvents>(
  event: K,
  ...props: AnalyticsEvents[K] extends Record<string, never>
    ? []
    : [props: AnalyticsEvents[K]]
): void {
  if (!posthog.__loaded) return;
  posthog.capture(event, props[0]);
}

/**
 * Capture a typed event via `sendBeacon`, for events likely to be a user's
 * last action before closing the tab (e.g. answering a survey right after
 * returning from an external ATS) rather than one followed by more
 * in-app activity. A plain `capture()` batches and flushes on its own
 * schedule, which can lose the race against an immediate tab close — see
 * `captureBeforeRedirect` below for the same problem when the "next thing"
 * is an in-app redirect instead of the user just leaving.
 */
export function captureReliable<K extends keyof AnalyticsEvents>(
  event: K,
  ...props: AnalyticsEvents[K] extends Record<string, never>
    ? []
    : [props: AnalyticsEvents[K]]
): void {
  if (!posthog.__loaded) return;
  posthog.capture(event, props[0], { send_instantly: true, transport: "sendBeacon" });
}

/**
 * Capture an event, then navigate away.
 *
 * A plain `capture()` followed by `window.location.href = …` races the beacon
 * and the step is simply lost — which is why checkout funnels routinely report
 * more purchases than checkout starts. `sendBeacon` is the platform's own
 * answer to exactly this: the browser takes ownership of the request and
 * delivers it even after the page is gone, so the redirect never has to wait
 * on analytics and the event never has to lose the race.
 */
export function captureBeforeRedirect<K extends keyof AnalyticsEvents>(
  event: K,
  props: AnalyticsEvents[K],
  redirect: () => void,
): void {
  if (posthog.__loaded) {
    posthog.capture(event, props, { send_instantly: true, transport: "sendBeacon" });
  }
  redirect();
}

/**
 * The browser's PostHog id, carried into Stripe metadata so the server-side
 * `purchase` event lands on the same person who did the searching. Empty string
 * when PostHog never loaded — the caller then stores nothing rather than a
 * placeholder that would look like a real id.
 */
export function currentDistinctId(): string {
  if (!posthog.__loaded) return "";
  return posthog.get_distinct_id() ?? "";
}

/** Person properties attached when a signed-in user is identified. */
export interface IdentifyProps {
  email?: string | null;
  /** Supabase `created_at` — when the account was created. */
  signup_date?: string;
  /** True for @concept.zone accounts — portfolio-wide executor/QA logins, never a real candidate. */
  is_internal_qa?: boolean;
  /** Puts the person in the project's "Internal / Test users" cohort — see internal-user.ts. */
  $internal_or_test_user?: true;
}

/**
 * Attach subsequent events to a signed-in account (person profile). Called when
 * the Supabase session is established/restored — i.e. right after the auth
 * callback lands the user back in the app. With `person_profiles:
 * "identified_only"`, nothing is created for anonymous visitors; identify() with
 * the same distinct id is an idempotent merge, so repeat calls are harmless.
 * No-ops until PostHog is loaded (consent-gated).
 */
export function identify(distinctId: string, props?: IdentifyProps): void {
  if (!posthog.__loaded) return;
  if (posthog.get_distinct_id() === distinctId) return; // already identified
  posthog.identify(distinctId, props);
}
